import axios, { type AxiosResponse } from '@fs/zion-axios'
import type { Playlist, PlaylistItem, PlaylistOrder, PlaylistsByItem } from './playlist.types'
import SERVICE from '../serviceName'

// DOCS: https://icseng.atlassian.net/wiki/x/JhX3Bw

const BASE = `${SERVICE}/playlists`
const config = { headers: { 'Content-Type': 'application/json' }, data: {} }

export const updatePlaylistItemsOrder = async ({
  playlistId,
  ordering,
}: {
  playlistId: string
  ordering: PlaylistOrder
}): Promise<AxiosResponse<PlaylistOrder>> => {
  return axios.put(`${BASE}/${playlistId}/order`, ordering, config)
}

export const updatePlaylist = async ({
  playlistId,
  data,
}: {
  playlistId: string
  data: { name: string; description: string }
}): Promise<AxiosResponse<Playlist>> => {
  return axios.put(`${BASE}/${playlistId}`, data, config)
}

export const getPlaylistItems = async ({
  playlistId,
}: {
  playlistId: string
}): Promise<AxiosResponse<PlaylistItem[]>> => {
  return axios.get(`${BASE}/${playlistId}/items`, config)
}

export const addPlaylistItem = async ({
  playlistId,
  sessionId,
}: {
  playlistId: string
  sessionId: string
}): Promise<AxiosResponse<PlaylistItem>> => {
  return axios.put(`${BASE}/${playlistId}/items/${sessionId}`, { id: sessionId }, config)
}

export const addMultipleItemsToPlaylist = async ({
  playlistId,
  sessionIds,
}: {
  playlistId: string
  sessionIds: string[]
}): Promise<AxiosResponse<PlaylistItem[]>> => {
  return axios.post(`${BASE}/${playlistId}/items`, sessionIds, config)
}

export const removePlaylistItem = async ({
  playlistId,
  sessionId,
}: {
  playlistId: string
  sessionId: string
}): Promise<AxiosResponse<void>> => {
  return axios.delete(`${BASE}/${playlistId}/items/${sessionId}`, null, config)
}

export const getPlaylists = async (offset?: string | null): Promise<AxiosResponse<Playlist[]>> => {
  return axios.get(`${BASE}/`, { ...config, params: offset ? { offset } : {} })
}

export const getPlaylistsForItem = async ({
  sessionId,
  signedIn,
}: {
  sessionId: string
  signedIn?: boolean
}): Promise<AxiosResponse<{ results: Playlist[] }>> => {
  if (signedIn) {
    return axios.get(`${BASE}/`, { ...config, params: { itemId: sessionId, includeItems: false } })
  }
  return { data: { results: [] } }
}

export const createPlaylist = async ({
  data,
}: {
  data: { name: string; description: string; initialItem?: string }
}): Promise<AxiosResponse<Playlist>> => {
  return axios.post(`${BASE}/`, { ...data, method: 'new' }, config)
}

export const followPlaylist = async ({ seriesId }: { seriesId: string }): Promise<AxiosResponse<Playlist>> => {
  return axios.post(`${BASE}/`, { sourcePlaylist: seriesId, method: 'follow' }, config)
}

export const getPlaylist = async ({
  playlistId,
  includeItems = true,
}: {
  playlistId: string
  includeItems?: boolean
}): Promise<AxiosResponse<Playlist>> => {
  return axios.get(`${BASE}/${playlistId}`, { ...config, params: { includeItems } })
}

export const deletePlaylist = async ({ playlistId }: { playlistId: string }): Promise<AxiosResponse<void>> => {
  return axios.delete(`${BASE}/${playlistId}`, config)
}

export const getDefaultPlaylist = async ({ includeItems = true }: { includeItems?: boolean } = {}): Promise<
  AxiosResponse<Playlist>
> => {
  return axios.get(`${BASE}/default`, { ...config, params: { includeItems } })
}

export const getPlaylistsByItems = async ({ ids }: { ids: string[] }): Promise<AxiosResponse<PlaylistsByItem>> => {
  return axios.get(`${BASE}/playlist-items`, { ...config, params: { ids: ids.join(',') } })
}
